import * as React from "react";
import { useTranslation } from "react-i18next";

import type { GenerateScriptsTarget } from "../../workspace/generate-scripts/GenerateScriptsModal.js";
import { OpportunityHubActionBar } from "../PersonaOpportunityHubChrome/OpportunityHubActionBar.js";
import { groupBySet } from "../PersonaOpportunityHubChrome/hub-fanout.js";
import type { HubChromeOpportunityRow } from "../PersonaOpportunityHubChrome/hub-chrome.types.js";

export interface OpportunityHubOpportunityActionsProps {
  rows: HubChromeOpportunityRow[];
  selectedIds: string[];
  canGenerate: boolean;
  onGenerate: (targets: GenerateScriptsTarget[]) => void;
  onClearSelection: () => void;
}

/** Opportunities bar: Generate Scripts and clear. The count stays on the breadcrumb pill. */
export function OpportunityHubOpportunityActions({
  rows,
  selectedIds,
  canGenerate,
  onGenerate,
  onClearSelection,
}: OpportunityHubOpportunityActionsProps) {
  const { t } = useTranslation();
  const targets = React.useMemo<GenerateScriptsTarget[]>(
    () =>
      groupBySet(rows, selectedIds).map((group) => ({
        setId: group.setId,
        setType: group.rows[0]?.setType ?? "PERSONA",
        angles: group.rows.map((row) => ({
          id: row.id,
          angle: row.angle,
          ...(row.personaName ? { personaTitle: row.personaName } : {}),
          supportingAd: row.thumbnailUrl ? { thumbnailUrl: row.thumbnailUrl } : null,
        })),
      })),
    [rows, selectedIds],
  );

  const selectedCount = targets.reduce((total, target) => total + target.angles.length, 0);

  return (
    <OpportunityHubActionBar
      selectedCount={selectedCount}
      selectedLabel=""
      showSelectionCopy={false}
      onClearSelection={onClearSelection}
      ariaLabel={t("personaOpportunities.hub.bulk.opportunitiesBarAria", {
        count: selectedCount,
        defaultValue_one: "Bulk actions for the selected opportunity",
        defaultValue_other: "Bulk actions for the selected opportunities",
      })}
      testId="opportunity-hub-opportunity-bar"
      actions={
        canGenerate
          ? [
              {
                id: "generate-scripts",
                label: t("personaOpportunities.hub.bulk.generateScripts", "Generate Scripts"),
                icon: "file-text",
                testId: "opportunity-hub-generate-scripts",
                onClick: () => onGenerate(targets),
              },
            ]
          : []
      }
    />
  );
}
